const collapseWhitespace = value =>
  typeof value === "string" ? value.trim().replace(/\s+/g, " ") : "";

const getAddressComponent = (components, types, useShortName = false) => {
  const component = (components || []).find(item =>
    types.some(type => item.types && item.types.includes(type))
  );

  if (!component) return "";
  return useShortName ? component.short_name : component.long_name;
};

export const normalizePostalCode = (postalCode, countryCode = "") => {
  const country = collapseWhitespace(countryCode).toUpperCase();
  const compact = collapseWhitespace(postalCode)
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");

  if (country === "CA" && compact.length === 6) {
    return `${compact.slice(0, 3)} ${compact.slice(3)}`;
  }

  if (country === "US" && compact.length === 9) {
    return `${compact.slice(0, 5)}-${compact.slice(5)}`;
  }

  if (country === "CA" || country === "US") return compact;

  return collapseWhitespace(postalCode).toUpperCase();
};

export const normalizeAddress = address => {
  const countryCode = collapseWhitespace(
    address.countryCode || address.country
  ).toUpperCase();

  return {
    address1: collapseWhitespace(address.address1),
    address2: collapseWhitespace(address.address2),
    city: collapseWhitespace(address.city),
    region: collapseWhitespace(address.region || address.province).toUpperCase(),
    postalCode: normalizePostalCode(address.postalCode, countryCode),
    countryCode,
    formattedAddress: collapseWhitespace(address.formattedAddress),
    placeId: collapseWhitespace(address.placeId),
    lat:
      typeof address.lat === "number" && Number.isFinite(address.lat)
        ? address.lat
        : null,
    lng:
      typeof address.lng === "number" && Number.isFinite(address.lng)
        ? address.lng
        : null,
    source: address.source === "google" ? "google" : "manual"
  };
};

export const addressFromGoogleSuggestion = suggestion => {
  if (!suggestion || !suggestion.gmaps) return null;

  const components = suggestion.gmaps.address_components || [];
  const streetNumber = getAddressComponent(components, ["street_number"]);
  const route = getAddressComponent(components, ["route"]);
  const premise = getAddressComponent(components, ["premise"]);
  const subpremise = getAddressComponent(components, ["subpremise"]);
  const postalCode = getAddressComponent(components, ["postal_code"]);
  const postalSuffix = getAddressComponent(components, ["postal_code_suffix"]);
  const countryCode = getAddressComponent(components, ["country"], true);

  const address1 = collapseWhitespace(
    [streetNumber, route].filter(Boolean).join(" ") ||
      premise ||
      suggestion.gmaps.name ||
      ""
  );

  return normalizeAddress({
    address1,
    address2: subpremise,
    city: getAddressComponent(components, [
      "locality",
      "postal_town",
      "sublocality",
      "administrative_area_level_2"
    ]),
    region: getAddressComponent(
      components,
      ["administrative_area_level_1"],
      true
    ),
    postalCode: postalSuffix ? `${postalCode}-${postalSuffix}` : postalCode,
    countryCode,
    formattedAddress: suggestion.gmaps.formatted_address,
    placeId: suggestion.placeId || suggestion.gmaps.place_id,
    lat: suggestion.location && suggestion.location.lat,
    lng: suggestion.location && suggestion.location.lng,
    source: "google"
  });
};

export const addressDisplayValue = address =>
  collapseWhitespace(
    address.formattedAddress ||
      [
        address.address1,
        address.city,
        [address.region || address.province, address.postalCode]
          .filter(Boolean)
          .join(" "),
        address.countryCode || address.country
      ]
        .filter(Boolean)
        .join(", ")
  );
